import { useRef, useState } from 'react';
import MapGl, { Layer, MapRef, Source, LayerProps } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import styled from 'styled-components';

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const mapStyle = 'mapbox://styles/boblberg/cl1agi71v008w14lcjuz9pxpm?optimize=true';

export const Map: React.FC<{ data: any[] }> = (props) => {
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const mapRef = useRef<MapRef>(null);

	const geojson: any = {
		type: 'FeatureCollection',
		features: getClusterPoints(props.data),
	};

	return (
		<StyledContainer className='map-container'>
			<MapGl
				ref={mapRef}
				mapStyle={mapStyle}
				onLoad={() => setIsLoaded(true)}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
				style={{ flex: 1 }}
			>
				{isLoaded && (
					<Source type="geojson" data={geojson} cluster={true} clusterMaxZoom={17} clusterRadius={10}>
						<Layer {...clusterLayer} />
						<Layer {...clusterCountLayer} />
						<Layer {...unclusteredPointLayer} />
						<Layer {...unclusteredCountLayer} />
					</Source>
				)}
			</MapGl>
		</StyledContainer>
	);
};

const StyledContainer = styled.div`
	margin: 0px 12px 24px 12px;

	.mapboxgl-canvas {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		width: 100%;
		height: 100%;
		border-radius: 12px;
	}

	.mapboxgl-control-container {
		display: none;
	}
`;

function getClusterPoints(data: Record<string, any>[]): Record<string, any>[] {
	const regex: RegExp = /[+-]?[\d.]+/g;
	const capitalMunicipalities: string[] = [
		'Gladsaxe',
		'Rødovre',
		'Albertslund',
		'Glostrup',
		'Københavns',
		'Herlev',
		'Gentofte',
		'Ballerup',
		'Lyngby-Taarbæk',
		'Brøndby',
		'Tårnby',
		'Frederiksberg',
		'Hvidovre',
		'Vallensbæk',
		'Furesø',
		'Rudersdal',
		'Dragør',
		'Ishøj',
		'Egedal',
		'Høje-Taastrup',
	];

	const upperCapitalMunicipalities: string[] = ['Fredensborg', 'Hørsholm', 'Hillerød', 'Allerød'];

	const eastZealandMunicipalities: string[] = ['Greve', 'Køge', 'Roskilde', 'Solrød'];

	const municipalityClusters: Record<string, any>[] = [
		{ municipalities: capitalMunicipalities, coordinates: [12.571786, 55.673955] },
		{ municipalities: upperCapitalMunicipalities, coordinates: [12.320007, 55.85496] },
		{ municipalities: eastZealandMunicipalities, coordinates: [12.25787, 55.587009] },
	];

	const clusterPoints: Record<string, any>[] = data.reduce(
		(clusterPoints: Record<string, any>[], { location, name }) => {
			let coordinates: number[] | undefined = municipalityClusters.find(({ municipalities }) => {
				return municipalities.includes(name);
			})?.coordinates;

			if (coordinates === undefined) {
				coordinates = location.match(regex).map(parseFloat);
			}

			if (coordinates?.length === 0) {
				return clusterPoints;
			}

			const point: Record<string, any> = {
				type: 'Feature',
				geometry: { type: 'Point', coordinates },
			};

			clusterPoints.push(point);
			return clusterPoints;
		},
		[]
	);

	return clusterPoints;
}

export const clusterLayer: LayerProps = {
	id: 'clusters',
	type: 'circle',
	filter: ['has', 'point_count'],
	paint: {
		'circle-color': ['step', ['get', 'point_count'], '#6675cd', 50, '#668fcd'],
		'circle-radius': ['step', ['get', 'point_count'], 15, 50, 18],
	},
};

export const clusterCountLayer: LayerProps = {
	id: 'cluster-count',
	type: 'symbol',
	filter: ['has', 'point_count'],
	layout: {
		'text-field': '{point_count_abbreviated}',
		'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
		'text-size': 20,
		'text-allow-overlap': true,
	},
	paint: {
		'text-color': '#ffffff',
	},
};

export const unclusteredPointLayer: LayerProps = {
	id: 'unclustered-point',
	type: 'circle',
	filter: ['!', ['has', 'point_count']],
	paint: {
		'circle-color': '#6675cd',
		'circle-radius': 15,
	},
};

export const unclusteredCountLayer: LayerProps = {
	id: 'unclustered-count',
	type: 'symbol',
	filter: ['!', ['has', 'point_count']],
	layout: {
		'text-field': '1',
		'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
		'text-size': 20,
		'text-allow-overlap': true,
	},
	paint: {
		'text-color': '#ffffff',
	},
};
